import { Moon, Sun } from 'lucide-react'

import { brand } from '@/brand/brand'
import { BrandMark } from '@/components/BrandMark'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useAuth } from '@/contexts/AuthContext'
import { useTheme } from '@/contexts/ThemeContext'

function getInitial(fullName?: string | null, email?: string): string {
  if (fullName && fullName.trim().length > 0) return fullName.trim()[0].toUpperCase()
  if (email && email.length > 0) return email[0].toUpperCase()
  return '?'
}

export function Header() {
  const { user, logout } = useAuth()
  const { theme, toggle } = useTheme()

  return (
    <header className="h-16 shrink-0 border-b border-border px-4 md:px-6 flex items-center justify-between">
      <div className="md:hidden flex items-center gap-2">
        <BrandMark size="sm" className="size-6" />
        <span className="text-lg font-semibold tracking-tight">{brand.productName}</span>
      </div>

      <div className="ml-auto flex items-center gap-2">
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={toggle}
                aria-label="Toggle theme"
              >
                {theme === 'dark' ? (
                  <Sun className="h-4 w-4" />
                ) : (
                  <Moon className="h-4 w-4" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>Toggle theme</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {user && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 rounded-full p-0" aria-label="User menu">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="text-xs font-medium">
                    {getInitial(user.full_name, user.email)}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel className="font-normal">
                <p className="text-xs text-muted-foreground truncate">{user.email}</p>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem disabled>Settings</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={logout}
              >
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </header>
  )
}
